import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom"; // Import useParams
import { getCourtRef, getDistrictCasesByCourtRef, getHighCasesByCourtRef, getOtherCourtCasesAddedManuallyByCourtRef, getSupremeCourtCasesByCourtRef } from "../../services/api/CasesApi.js";
import { getLawyers } from "../../services/api/LawyersApi.js";
// Update your saveTask import line to include updateTaskCompletion
import { saveTask, updateTask, getTaskById, updateTaskCompletion } from "../../services/api/TaskApi.js";
import { CompositeDecorator, ContentState, Editor, EditorState, Modifier, RichUtils, convertToRaw, convertFromRaw } from "draft-js";
import "draft-js/dist/Draft.css";
import "./TaskForm.scss";
import {
  FaCalendarAlt as Calendar,
  FaClock as Clock,
  FaUpload as Upload,
  FaBold as Bold,
  FaItalic as Italic,
  FaUnderline as Underline,
  FaListUl as List,
  FaCode as Code,
  FaLink as Link,
  FaImage as Image,
  FaQuoteRight as Quote,
} from "react-icons/fa";

// 1. Add these imports to your existing imports
import {
  FaCheckCircle as CheckCircle,
  FaClock as ClockIcon,
  FaExclamationTriangle as Warning,
} from "react-icons/fa";

import {
  getDownloadURL,
  ref,
  storage,
  uploadBytesResumable,
} from "../../utils/firebase.js";
import { useToast } from "../../context/ToastContext.js";
import { useAuth } from "../../context/AuthContext.js";
import { getHierarchy } from "../../services/api/HierarchyApi.js";
import { getBranchDetailsById } from "../../services/api/BranchApi.js";
import { BiCheck } from "react-icons/bi";
import { IoClose } from "react-icons/io5";
import { useBubbleSettings } from "../../context/BubbleSettingsContext.js";
import { uploadFileWithProgress } from "../../utils/ftpUploadUtils.js";


export const TaskForm = ({ onSuccess, onCancel, disabledCourtRef = false, initialData = null, isModal = false }) => {
  const { id } = useParams();


  const fileInputRef = useRef(null);
  const taskNameRef = useRef(null);
  const hasFocusedRef = useRef(false);
  const dropdownRef = useRef();
  const modalRef = useRef(null);
  const { company } = useAuth();
  const editorRef = useRef(null);
  const { showToast } = useToast();
  const { fetchAndUpdateBubbleSettings } = useBubbleSettings();
  const isEnt = company?.type === "ent";
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // 2. Add these new state variables after your existing state declarations
  const [isCompleted, setIsCompleted] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkSelection, setLinkSelection] = useState(null);
  const [activeTab, setActiveTab] = useState("oneTime");
  const [assignedTo, setAssignedTo] = useState([]);
  const [allLawyers, setAllLawyers] = useState([]);
  const [selectedLawyer, setSelectedLawyer] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedCourtTab, setSelectedCourtTab] = useState('high');
  const [associatedCases, setAssociatedCases] = useState([]);
  const [branchesWithNames, setBranchesWithNames] = useState([]);
  const [assignedByName, setAssignedByName] = useState("");
  const [assignedToNames, setAssignedToNames] = useState([]);


  // Add this state to track if initial data has been loaded
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  // 3. Add task status options configuration (simplified for binary status)
  const taskStatusOptions = [
    { value: false, label: 'Pending', icon: ClockIcon, color: '#f59e0b' },
    { value: true, label: 'Completed', icon: CheckCircle, color: '#10b981' },
  ];

  const taskId = id || initialData?.id || initialData?._id;

  const handleStatusUpdate = async (completionStatus) => {
    if (!taskId) return;

    setIsUpdatingStatus(true);
    try {
      await updateTaskCompletion(taskId, completionStatus);
      setIsCompleted(completionStatus);
      const statusLabel = completionStatus ? 'Completed' : 'Pending';
      showToast(`Task marked as ${statusLabel}`, 'success');
      await fetchAndUpdateBubbleSettings();
    } catch (error) {
      console.error("Error updating task status:", error);
      showToast('Failed to update task status', 'error');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const getStatusInfo = (completed) => {
    return taskStatusOptions.find(opt => opt.value === completed) || taskStatusOptions[0];
  };

  const isEditMode = !!(taskId || initialData?._id);

  const getCurrentDateTime = () => {
    const now = new Date();
    const date = now.toISOString().split('T')[0]; // YYYY-MM-DD format
    const time = now.toTimeString().slice(0, 5); // HH:MM format
    return { date, time };
  };

  const [formData, setFormData] = useState(() => {
    // For modal mode with new task, set defaults immediately
    if (isModal && initialData?.courtRef && !initialData._id && !id) {
      const now = new Date();
      const date = now.toISOString().split('T')[0];
      const time = now.toTimeString().slice(0, 5);
    
      return {
        taskName: "",
        associatedCase: initialData.courtRef || "",
        assignedTo: "",
        dueDate: date,
        dueTime: time,
        repetitionFrequency: "",
        documentUrl: "",
        courtRef: initialData.courtRef || "",
        branchId: "",
        recurringDays: []
      };
    }
  
    // Default initialization for non-modal or when no initial data
    const { date, time } = getCurrentDateTime();
    return {
      taskName: "",
      associatedCase: "",
      assignedTo: "",
      dueDate: date,
      dueTime: time,
      repetitionFrequency: "",
      documentUrl: "",
      courtRef: "",
      branchId: "",
      recurringDays: []
    };
  });


  const navigate = useNavigate();


  const linkDecorator = new CompositeDecorator([
    {
      strategy: (contentBlock, callback, contentState) => {
        contentBlock.findEntityRanges((character) => {
          const entityKey = character.getEntity();
          return (
            entityKey !== null &&
            contentState.getEntity(entityKey).getType() === "LINK"
          );
        }, callback);
      },
      component: (props) => {
        const entity = props.contentState.getEntity(props.entityKey);
        const { url } = entity.getData();


        const handleLinkClick = (e) => {
          e.preventDefault();
          e.stopPropagation();


          // Check if URL is valid and properly formatted
          let finalUrl = url;
          if (!url.startsWith('http://') && !url.startsWith('https://')) {
            finalUrl = 'https://' + url;
          }


          // Open in new tab/window
          window.open(finalUrl, '_blank', 'noopener,noreferrer');
        };


        return (
          <a
            href={url.startsWith('http') ? url : `https://${url}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#0066cc',
              textDecoration: 'underline',
              cursor: 'pointer',
              backgroundColor: 'rgba(0, 102, 204, 0.1)',
              padding: '1px 2px',
              borderRadius: '2px'
            }}
            onClick={handleLinkClick}
            onMouseDown={(e) => e.preventDefault()} // Prevent editor selection changes
          >
            {props.children}
          </a>
        );
      },
    },
  ]);


  const [editorState, setEditorState] = useState(() => EditorState.createEmpty(linkDecorator));
 
  useEffect(() => {
    const fetchBranchNames = async () => {
      if (!company?.branches || company.branches.length === 0) return;


      try {
        const fetchedBranches = await Promise.all(
          company.branches.map(async (id) => {
            const res = await getBranchDetailsById(id);
            return {
              id,
              name: res?.data?.result?.branchData?.branchName || id
            };
          })
        );
        setBranchesWithNames(fetchedBranches);
      } catch (error) {
        console.error("Failed to fetch branch names", error);
      }
    };


    fetchBranchNames();
  }, [company?.branches]);


  useEffect(() => {
    const fetchAssociatedCases = async () => {
      try {
        let response;


        if (selectedCourtTab === 'high') {
          response = await getHighCasesByCourtRef();
        } else if (selectedCourtTab === 'district') {
          response = await getDistrictCasesByCourtRef();
        } else if (selectedCourtTab === 'other') {
          response = await getOtherCourtCasesAddedManuallyByCourtRef();
        } else if (selectedCourtTab === 'supreme') {
          response = await getSupremeCourtCasesByCourtRef();
        }


        setAssociatedCases(response?.data || []);
      } catch (err) {
        console.error('Error fetching cases:', err);
        setAssociatedCases([]); // fallback to empty if error
      }
    };


    fetchAssociatedCases();
  }, [selectedCourtTab]);


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  const handleSelect = (courtRef) => {
    setFormData((prev) => ({
      ...prev,
      associatedCase: courtRef,
    }));
    setIsDropdownOpen(false);
  };
 
  const [isInitialized, setIsInitialized] = useState(false);


  useEffect(() => {
    const fetchInitialData = async () => {
      // Only run for editing existing tasks, not for new tasks
      if (taskId || (initialData?._id)) {
        try {
          let task;
          let lawyersData = [];
          if (taskId && !initialData) {
            const [taskRes, lawyersRes] = await Promise.all([
              getTaskById(taskId),
              getHierarchy()
            ]);
            task = taskRes.data;
            lawyersData = lawyersRes.data.users || [];
            setAllLawyers(lawyersData);
          } else if (initialData?._id) {
            task = initialData;
            const lawyersRes = await getHierarchy();
            lawyersData = lawyersRes.data.users || [];
            setAllLawyers(lawyersData);
          }


          if (task) {

            // 5. Update your fetchInitialData useEffect to include status loading
            setIsCompleted(task.isCompleted || false);

            // Set assignedTo immediately when lawyers data is available
            if (task.assignedTo && Array.isArray(task.assignedTo)) {
              // Use the freshly fetched lawyers data
              const hydratedAssignees = lawyersData.filter(lawyer =>
                task.assignedTo.includes(lawyer._id)
              );
              setAssignedTo(hydratedAssignees);
            }


            if (task.recurringTask) {
              setActiveTab("recurring");
            } else {
              setActiveTab("oneTime");
            }


            setFormData({
              taskName: task.taskName || "",
              branchId: task.branchId || "",
              associatedCase: task.courtRef || initialData?.courtRef || "",
              courtRef: task.courtRef || initialData?.courtRef || "",
              assignedTo: task.assignedTo || [],
              dueDate: task.dueDate ? task.dueDate.split("T")[0] : "",
              dueTime: task.dueDate ? (() => {
                const date = new Date(task.dueDate);
                const hours = date.getHours().toString().padStart(2, '0');
                const minutes = date.getMinutes().toString().padStart(2, '0');
                return `${hours}:${minutes}`;
              })() : "",
              repetitionFrequency: task?.recurringTask?.frequency ||
                (task?.recurringTask?.in === "day" ? "daily" :
                  task?.recurringTask?.in === "week" ? "weekly" :
                    task?.recurringTask?.in === "month" ? "monthly" : ""),
              documentUrl: task.document || "",
              recurringDays: task?.recurringTask?.days || []
            });


            // Load description from JSON string if present
            let contentState;
            if (task.description && typeof task.description === 'string') {
              try {
                const raw = JSON.parse(task.description);
                if (raw && raw.blocks) {
                  contentState = convertFromRaw(raw);
                } else {
                  contentState = ContentState.createFromText(task.description);
                }
              } catch (e) {
                contentState = ContentState.createFromText(task.description);
              }
            } else if (task.description && typeof task.description === 'object' && task.description.blocks) {
              contentState = convertFromRaw(task.description);
            } else {
              contentState = ContentState.createFromText(task.description || "");
            }
            setEditorState(EditorState.createWithContent(contentState, linkDecorator));
          }


        } catch (error) {
          console.error("Error fetching task data:", error);
        }
        finally {
          // mark that initial fetch attempt finished so focus logic can run
          setIsInitialized(true);
        }
      } else {
        // For new tasks, just fetch lawyers data
        try {
          if (allLawyers.length === 0) {
            const lawyersRes = await getHierarchy();
            setAllLawyers(lawyersRes?.data?.users || []);
          }
        } catch (error) {
          console.error("Error fetching lawyers data:", error);
        }
      }
    };


    fetchInitialData();
  }, [taskId, initialData?._id]); // Simplified dependencies


useEffect(() => {
  if (hasFocusedRef.current) return;
  
  let shouldFocus = false;
  
  if (taskId || initialData?._id) {
    // For editing existing tasks, wait for initialization to complete
    shouldFocus = isInitialized;
  } else {
    // For new tasks
    if (isModal && initialData?.courtRef && !initialData?._id) {
      // Modal with prefilled courtRef - wait for initial data to load
      shouldFocus = initialDataLoaded;
    } else {
      // Regular new task creation - focus immediately
      shouldFocus = true;
    }
  }

  if (!shouldFocus) return;

  // Add a small delay to ensure DOM is ready
  const focusTimeout = setTimeout(() => {
    const input = taskNameRef.current;
    if (input && !hasFocusedRef.current) {
      input.focus();
      const val = input.value || '';
      try {
        input.setSelectionRange(val.length, val.length);
      } catch (e) {
        // Ignore selection range errors
      }
      hasFocusedRef.current = true;
    }
  }, 100); // 100ms delay

  return () => clearTimeout(focusTimeout);
}, [isInitialized, initialDataLoaded, taskId, initialData?._id, isModal, initialData?.courtRef]);


useEffect(() => {
  // Focus task name input when switching tabs
  const focusTimeout = setTimeout(() => {
    const input = taskNameRef.current;
    if (input) {
      input.focus();
      const val = input.value || '';
      try {
        input.setSelectionRange(val.length, val.length);
      } catch (e) {
        // Ignore selection range errors
      }
    }
  }, 50); // Shorter delay for tab switching

  return () => clearTimeout(focusTimeout);
}, [activeTab]); // Trigger when activeTab changes



  useEffect(() => {
    if (editorRef.current) {
      const editorNode = editorRef.current.editor;
      if (editorNode) {
        const currentSelection = editorState.getSelection();
        setEditorState(EditorState.forceSelection(editorState, currentSelection));
      }
    }
  }, [editorState.getCurrentContent().getEntityMap()]);


  useEffect(() => {
    // Special handling for modal mode with initialData that has courtRef but no task data
    if (isModal && initialData?.courtRef && !taskId && !initialData._id && !initialDataLoaded) {
      const { date, time } = getCurrentDateTime();
      setFormData(prev => ({
        ...prev,
        dueDate: date,
        dueTime: time,
        courtRef: initialData.courtRef,
        associatedCase: initialData.courtRef
      }));
      setInitialDataLoaded(true);
    }
  }, [isModal, initialData?.courtRef, taskId, initialData?._id, initialDataLoaded]);


  const handleEditorChange = (state) => {
    // Ensure the decorator is maintained
    const newState = EditorState.set(state, { decorator: linkDecorator });
    setEditorState(newState);
  };


  const handleKeyCommand = (command, editorState) => {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      handleEditorChange(newState);
      return "handled";
    }
    return "not-handled";
  };


  const toggleInlineStyle = (style) => {
    handleEditorChange(RichUtils.toggleInlineStyle(editorState, style));
  };


  const toggleBlockType = (blockType) => {
    handleEditorChange(RichUtils.toggleBlockType(editorState, blockType));
  };


  const focusEditor = () => editorRef.current?.focus();


  const handleTabChange = (tab) => setActiveTab(tab);


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };


  const handleDateChange = (date) => {
    setFormData((prev) => ({ ...prev, dueDate: date }));
  };


  const handleTimeChange = (time) => {
    setFormData((prev) => ({ ...prev, dueTime: time }));
  };


  const handleRemoveAssignee = (id) => {
    setAssignedTo((prev) => prev.filter((person) => person._id !== id));
  };


  const handleAddAssignee = (e) => {
    const selectedId = e.target.value;
    setSelectedLawyer(selectedId);
    const lawyer = allLawyers.find((lawyer) => lawyer._id === selectedId);
    if (lawyer && !assignedTo.some((person) => person._id === selectedId)) {
      setAssignedTo([...assignedTo, lawyer]);
    }
  };


  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      handleFileUpload(selectedFile);
    }
  };


  // Modified handleFileUpload to use functional updates
  const handleFileUpload = async (selectedFile) => {
    if (!selectedFile) return;


    setUploading(true);
    setUploadProgress(0);


    try {
      // Generate file path based on your requirements
      const filePath = company._id ? `/${company._id}/others` : `/unknown`;


      await uploadFileWithProgress(
        selectedFile,
        filePath,
        // Progress callback
        (progress) => {
          setUploadProgress(progress);
        },
        // Success callback
        (result) => {
          // Use functional update to preserve other form data
          setFormData((prevFormData) => ({
            ...prevFormData,
            documentUrl: result.imageUrl // This is the processed URL part
          }));
          setUploading(false);
          showToast('File uploaded successfully!', 'success');
        },
        // Error callback
        (error) => {
          console.error("File upload failed:", error);
          setUploading(false);
          setUploadProgress(0);
          showToast(error.message || 'File upload failed', 'error');
        }
      );


    } catch (error) {
      console.error("File upload failed:", error);
      setUploading(false);
      setUploadProgress(0);
      showToast(error.message || 'File upload failed', 'error');
    }
  };


  // Modified handleCancelUpload to use functional updates
  const handleCancelUpload = () => {
    setFile(null);
    setFormData((prevFormData) => ({
      ...prevFormData,
      documentUrl: ""
    }));
    setUploadProgress(0);
    setUploading(false);
  };

  const handleFormKeyDown = (e) => {
  // Check if Enter was pressed and form is valid
  if (e.key === 'Enter' && !e.shiftKey) {
    // Don't submit if we're in certain elements that need Enter for their functionality
    const target = e.target;
    const tagName = target.tagName.toLowerCase();
    const isTextarea = tagName === 'textarea';
    const isSelect = tagName === 'select';
    const isInRichTextEditor = target.closest('.DraftEditor-root');
    const isInLinkInput = target.closest('.link-input-panel');
    
    // Allow Enter in rich text editor, textareas, selects, and link input
    if (isTextarea || isSelect || isInRichTextEditor || isInLinkInput) {
      return;
    }
    
    // Prevent default form submission and submit manually if form is valid
    e.preventDefault();
    
    if (isFormValid() && !isSubmitting) {
      handleSubmit(e);
    }
  }
};


  const handleUploadClick = () => {
    fileInputRef.current.click();
  };
 
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);


    try {
      let dueDateTime = null;


      if (formData.dueDate) {
        dueDateTime = new Date(formData.dueDate);


        if (typeof formData.dueTime === 'string' && formData.dueTime.includes(':')) {
          const [hours, minutes] = formData.dueTime.split(':').map(Number);
          dueDateTime.setHours(hours);
          dueDateTime.setMinutes(minutes);
          dueDateTime.setSeconds(0);
          dueDateTime.setMilliseconds(0);
        }
      }


      // Save description as JSON string (Draft.js raw)
      const rawDescription = convertToRaw(editorState.getCurrentContent());
      const stringifiedDescription = JSON.stringify(rawDescription);


      const taskData = {
        taskName: formData.taskName,
        associatedCase: formData.courtRef,
        courtRef: formData.associatedCase,
        description: stringifiedDescription, // store as string
        dueDate: dueDateTime ? dueDateTime.toISOString() : null,
        document: formData.documentUrl,
        assignedTo: assignedTo.length > 0 ? assignedTo.map(user => user._id) : null,
        // 6. Update your handleSubmit function to include status
        isCompleted: isCompleted, // Include completion status in task data
        ...(isEnt && { branchId: formData.branchId }),
      };


      if (
        activeTab === "recurring" &&
        formData.repetitionFrequency
      ) {
        taskData.recurringTask = {
          frequency: formData.repetitionFrequency,
          days: formData.recurringDays
        };
      }


      Object.keys(taskData).forEach((key) => {
        if (
          (taskData[key] === "" ||
            taskData[key] === null ||
            taskData[key] === undefined) &&
          key !== "courtRef"
        ) {
          delete taskData[key];
        }
      });


      let response;


      if (taskId) {
        response = await updateTask(taskData, taskId);
        showToast('Task updated successfully!', 'success');
        // For updates, refresh immediately
        await fetchAndUpdateBubbleSettings();
      } else {
        response = await saveTask(taskData);
        showToast('Task created successfully!', 'success');
        // For new tasks, add a delay to ensure database consistency
        await new Promise(resolve => setTimeout(resolve, 1000)); // 1 second delay
        await fetchAndUpdateBubbleSettings();
      }


      setSubmitSuccess(true);


      if (!taskId) {
        setFormData({
          taskName: "",
          associatedCase: "",
          assignedTo: "",
          dueDate: "",
          dueTime: "",
          repetitionFrequency: "",
          documentUrl: "",
          courtRef: "",
          branchId: "",
          recurringDays: []
        });
        setAssignedTo([]);
        setSelectedLawyer("");
        setEditorState(EditorState.createEmpty(linkDecorator));
        setFile(null);
        // 7. Reset status in form reset (add this line where you reset other form data):
        setIsCompleted(false); // Reset status for new tasks
      }


      if (onSuccess) {
        onSuccess(formData.associatedCase || formData.courtRef);
      } else {
        navigate('/home/tasks');
      }


    } catch (error) {
      console.error("Error saving task:", error);
      setSubmitError(
        error.response?.data?.message ||
        "Failed to save task. Please try again."
      );
      showToast('Failed to save task', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };
 
  const handleSendNotification = async () => {
    showToast("Notification functionality will be implemented in the future.", "error");
  };


  const handleLinkClick = () => {
    const selection = editorState.getSelection();
    if (!selection.isCollapsed()) {
      setLinkSelection(selection);
      setShowLinkInput(true);
      setLinkUrl("");
    } else {
      showToast("Please select some text to add a hyperlink.", "error");
    }
  };
 
  const cancelLinkInsertion = () => {
    setShowLinkInput(false);
    setLinkUrl("");
    setLinkSelection(null);
  };


  const insertLink = () => {
    if (!linkUrl || !linkSelection) return;


    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity("LINK", "MUTABLE", { url: linkUrl });
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();


    const newContentState = Modifier.applyEntity(
      contentStateWithEntity,
      linkSelection,
      entityKey
    );


    const newEditorState = EditorState.push(
      editorState,
      newContentState,
      'apply-entity'
    );


    const finalEditorState = EditorState.forceSelection(
      newEditorState,
      newContentState.getSelectionAfter()
    );


    setEditorState(finalEditorState);


    setShowLinkInput(false);
    setLinkUrl("");
    setLinkSelection(null);


    setTimeout(() => {
      if (editorRef.current) {
        editorRef.current.focus();
      }
    }, 0);
  };


  const handleCancelTasks = useCallback(() => {
    if (onCancel) {
      // Modal scenario
      onCancel();
    } else {
      // Route scenario
      navigate("/home/tasks");
    }
  }, [onCancel, navigate]);


  // Handle click outside modal to close
  useEffect(() => {
    const handleModalClickOutside = (event) => {
      if (isModal && modalRef.current && !modalRef.current.contains(event.target)) {
        handleCancelTasks();
      }
    };


    if (isModal) {
      document.addEventListener('mousedown', handleModalClickOutside);
    }


    return () => {
      document.removeEventListener('mousedown', handleModalClickOutside);
    };
  }, [isModal, handleCancelTasks]);


  // Add these state variables to your component
  const [taskSearchTerm, setTaskSearchTerm] = useState('');
  const [taskFilteredCases, setTaskFilteredCases] = useState(associatedCases);
  const taskInputRef = useRef(null);


  useEffect(() => {
    // Filter cases based on search term and court tab
    let filtered = Array.isArray(associatedCases) ? associatedCases : [];


    // Filter by search term
    if (taskSearchTerm.trim()) {
      filtered = filtered.filter(caseItem =>
        caseItem.parties?.toLowerCase().includes(taskSearchTerm.toLowerCase()) ||
        caseItem.courtRef?.toLowerCase().includes(taskSearchTerm.toLowerCase()) ||
        caseItem.caseDate?.caseTypeAndNumber?.toLowerCase().includes(taskSearchTerm.toLowerCase())
      );
    }


    setTaskFilteredCases(filtered);
  }, [taskSearchTerm, associatedCases, selectedCourtTab]);


  // Helper functions
  const getTaskDisplayValue = () => {
    if (taskSearchTerm && isDropdownOpen) {
      return taskSearchTerm;
    }


    if (!formData.associatedCase) return "";


    const selected = associatedCases.find(
      (item) => String(item.courtRef).trim() === String(formData.associatedCase).trim()
    );
    return selected ? `${selected.courtRef}` : formData.associatedCase;
  };


  const handleTaskInputChange = (e) => {
    const value = e.target.value;
    setTaskSearchTerm(value);
    setIsDropdownOpen(true);


    // Clear selection if user is typing
    if (value !== getTaskDisplayValue()) {
      // Clear the associated case if user is searching
      // You may want to handle this differently based on your form logic
    }
  };


  const handleTaskInputFocus = () => {
    setIsDropdownOpen(true);
    setTaskSearchTerm('');
  };


  const handleTaskCaseSelect = (caseItem) => {
    handleSelect(caseItem.courtRef); // Use your existing function
    setTaskSearchTerm('');
    setIsDropdownOpen(false);

  //   setTimeout(() => {
  //   if (taskInputRef.current) {
  //     taskInputRef.current.focus();
  //   }
  // }, 100);
  };

  const handleClearCase = () => {
    setFormData((prev) => ({
      ...prev,
      associatedCase: "",
    }));
    setTaskSearchTerm('');
    setIsDropdownOpen(false);
  };


  const handleTaskKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsDropdownOpen(false);
      taskInputRef.current?.blur();
    } else if (e.key === 'Enter' && taskFilteredCases.length === 1) {
      handleTaskCaseSelect(taskFilteredCases[0]);
    }
  };


  const isFormValid = () => {
    return formData.taskName.trim() !== '' &&
      //  formData.dueDate !== '' &&
      //  formData.dueTime !== '' &&
      !uploading; // Also disabled during file upload
  };
 
  return (
    <div className={`task-form-container ${isModal ? 'modal-mode-task-content-task-form-container' : ''}`} ref={modalRef}>
      <div className={`task-form-wrapper ${isModal ? 'modal-mode-task-content-task-form-wrapper' : ''}`}>
        {/* Move header inside the wrapper */}
        <div className="task-form-header">
          <h1 className={`task-form-title ${isModal ? 'modal-mode-task-content-task-form-title' : ''}`}>{id ? "Update Task" : "Create New Task"}</h1>
          {isModal && (
            <button
              className="task-form-close-button"
              onClick={handleCancelTasks}
              aria-label="Close"
            >
              &times;
            </button>
          )}
        </div>


        {isEditMode && (
          <div className="task-status-section">
            <div className="current-status">
              <span className="status-label">Current Status:</span>
              <div className="status-badge" style={{ backgroundColor: getStatusInfo(isCompleted).color }}>
                {React.createElement(getStatusInfo(isCompleted).icon, { size: 14 })}
                <span>{getStatusInfo(isCompleted).label}</span>
              </div>
            </div>
            
            <div className="status-actions">
              <span className="status-label">Update Status:</span>
              <div className="status-buttons">
                {taskStatusOptions.map((option) => (
                  <button
                    key={option.value.toString()}
                    type="button"
                    className={`status-button ${isCompleted === option.value ? 'active' : ''}`}
                    onClick={() => handleStatusUpdate(option.value)}
                    disabled={isUpdatingStatus || isCompleted === option.value}
                    style={{ 
                      borderColor: option.color,
                      color: isCompleted === option.value ? '#fff' : option.color,
                      backgroundColor: isCompleted === option.value ? option.color : 'transparent'
                    }}
                  >
                    {React.createElement(option.icon, { size: 14 })}
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        <hr className="task-form-divider" />


        <div className="task-tabs">
          <button
            className={`tab-button ${activeTab === "oneTime" ? "active" : ""}`}
            onClick={() => handleTabChange("oneTime")}
            type="button"
          >
            One Time Task
          </button>
          <button
            className={`tab-button ${activeTab === "recurring" ? "active" : ""}`}
            onClick={() => handleTabChange("recurring")}
            type="button"
          >
            Recurring Task
          </button>
        </div>


        {submitSuccess && (
          <div
            style={{
              backgroundColor: "#d1fae5",
              padding: "10px",
              color: "#065f46",
              borderRadius: "5px",
              marginBottom: "1rem",
            }}
            role="alert"
          >
            Task {id ? "updated" : "created"} successfully!
          </div>
        )}


        {submitError && (
          <div
            style={{
              backgroundColor: "#fee2e2",
              padding: "10px",
              color: "#b91c1c",
              borderRadius: "5px",
              marginBottom: "1rem",
            }}
            role="alert"
          >
            {submitError}
          </div>
        )}


        <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown} noValidate className={`form-content ${isModal ? 'modal-mode-task-content' : ''}`}>


          {/* Basic Information Section */}
          <div className="form-section">
            <h3>Basic Information</h3>


            <div className="form-group">
              <label htmlFor="taskName" className="label-task">
                Task: <span className="required">*</span>
              </label>
              <input
                ref={taskNameRef}
                id="taskName"
                name="taskName"
                placeholder="Enter Task Name"
                type="text"
                maxLength={150}
                value={formData.taskName}
                onChange={handleInputChange}
                className="textInput"
                required
                autoComplete="off"
              />
            </div>


            {activeTab === "recurring" && (
              <>
                <div className="form-group">
                  <label className="label-task">Frequency</label>
                  <select
                    name="repetitionFrequency"
                    value={formData.repetitionFrequency}
                    onChange={handleInputChange}
                    className="frequency-dropdown"
                  >
                    <option value="">Select frequency</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                  </select>
                </div>


                {formData.repetitionFrequency === "weekly" && (
                  <div className="form-group">
                    <label className="label-task">Days</label>
                    <div className="days-selector">
                      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                        <label key={day} className="day-checkbox">
                          <input
                            type="checkbox"
                            checked={formData.recurringDays.includes(day)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({
                                  ...formData,
                                  recurringDays: [...formData.recurringDays, day]
                                });
                              } else {
                                setFormData({
                                  ...formData,
                                  recurringDays: formData.recurringDays.filter(d => d !== day)
                                });
                              }
                            }}
                          />
                          {day}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}


            {activeTab !== "recurring" && (
              <div className="date-time-wrapper">
                <div className="form-group date-time-group">
                  <label htmlFor="dueDate" className="label-task">
                    Due Date: <span className="required">*</span>
                  </label>
                  <input
                    id="dueDate"
                    type="date"
                    value={formData.dueDate}
                    onChange={(e) => handleDateChange(e.target.value)}
                    className="date-time-input-task"
                    required
                  />
                </div>
                <div className="form-group date-time-group">
                  <label htmlFor="dueTime" className="label-task">
                    Due Time: <span className="required">*</span>
                  </label>
                  <div className="time-div-task-content">
                    <input
                      id="dueTime"
                      type="time"
                      value={formData.dueTime}
                      onChange={(e) => handleTimeChange(e.target.value)}
                      className="date-time-input-task"
                      step="900"
                      required
                    />
                  </div>
                </div>
              </div>
            )}


            <div className="form-group">
              <label className="label-task">Description:</label>
              <div className="rich-text-editor">
                <div className="editor-toolbar">
                  <button type="button" onClick={() => toggleInlineStyle("BOLD")} aria-label="Bold">
                    <Bold size={16} />
                  </button>
                  <button type="button" onClick={() => toggleInlineStyle("ITALIC")} aria-label="Italic">
                    <Italic size={16} />
                  </button>
                  <button type="button" onClick={() => toggleInlineStyle("UNDERLINE")} aria-label="Underline">
                    <Underline size={16} />
                  </button>
                  <span className="divider" />
                  <button type="button" onClick={() => toggleBlockType("unordered-list-item")} aria-label="Unordered List">
                    <List size={16} />
                  </button>
                  <span className="divider" />
                  <button type="button" aria-label="Insert Link" onClick={handleLinkClick}>
                    <Link size={16} />
                  </button>
                </div>


                {showLinkInput && (
                  <div className="link-input-panel">
                    <div className="link-input-container">
                      <input
                        type="url"
                        value={linkUrl}
                        onChange={(e) => setLinkUrl(e.target.value)}
                        placeholder="Enter URL (e.g., https://example.com)"
                        className="link-input"
                        autoFocus
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            insertLink();
                          } else if (e.key === 'Escape') {
                            cancelLinkInsertion();
                          }
                        }}
                      />
                      <div className="link-actions">
                        <button type="button" onClick={insertLink} className="link-confirm-btn" disabled={!linkUrl}>
                          <BiCheck size={14} />
                        </button>
                        <button type="button" onClick={cancelLinkInsertion} className="link-cancel-btn">
                          <IoClose size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                )}


                <div className="editor-container" onClick={focusEditor}>
                  <Editor
                    ref={editorRef}
                    editorState={editorState}
                    onChange={handleEditorChange}
                    handleKeyCommand={handleKeyCommand}
                    placeholder="Enter description here..."
                  />
                </div>
              </div>
            </div>
          </div>


          {/* Assignment Section */}
          <div className="form-section">
            <h3>Assignment</h3>


            <div className="form-group">
              <label className="label-task">Task Assigned To:
              <span className="required">*</span>
              </label>
              <div className="assignee-section">
                <div className="dropdown-container">
                  <select
                    value={selectedLawyer}
                    onChange={handleAddAssignee}
                    className="selectInput"
                  >
                    <option value="">Select assignee</option>
                    {allLawyers.map((lawyer) => (
                      <option key={lawyer._id} value={lawyer._id}>
                        {lawyer.name} ({lawyer.email})
                      </option>
                    ))}
                  </select>
                </div>


                {assignedTo.length > 0 && (
                  <div className="assignee-tags">
                    {assignedTo.map((person) => (
                      <div key={person._id} className="assignee-tag">
                        <span>{person.name}</span>
                        <button
                          type="button"
                          className="remove-assignee"
                          onClick={() => handleRemoveAssignee(person._id)}
                          aria-label={`Remove ${person.name}`}
                        >
                          x
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>


          {/* Case Association Section */}
          <div className="form-section">
            <h3>Case Association</h3>


            <div className="form-group">
              <div className="case-selector" ref={dropdownRef}>
                <label className="label-task">Associated Case:</label>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <input
                    ref={taskInputRef}
                    type="text"
                    value={getTaskDisplayValue()}
                    onChange={handleTaskInputChange}
                    onFocus={handleTaskInputFocus}
                    onKeyDown={handleTaskKeyDown}
                    className="custom-dropdown-input"
                    placeholder="Type to search cases..."
                    autoComplete="off"
                    disabled={disabledCourtRef && isModal && formData.associatedCase}
                  />
                  {formData.associatedCase && !(disabledCourtRef && isModal) && (
                    <button
                      type="button"
                      onClick={handleClearCase}
                      className="clear-case-button"
                      aria-label="Clear selection"
                      title="Clear selection"
                    >
                      ×
                    </button>
                  )}
                </div>


                {isDropdownOpen && !(disabledCourtRef && isModal && formData.associatedCase) && (
                  <div className="dropdown-content">
                    <div className="dropdown-tabs">
                      <button
                        type="button"
                        className={selectedCourtTab === 'high' ? 'active' : ''}
                        onClick={() => setSelectedCourtTab('high')}
                      >
                        High Court
                      </button>
                      <button
                        type="button"
                        className={selectedCourtTab === 'district' ? 'active' : ''}
                        onClick={() => setSelectedCourtTab('district')}
                      >
                        District Court
                      </button>
                      <button
                        type="button"
                        className={selectedCourtTab === 'other' ? 'active' : ''}
                        onClick={() => setSelectedCourtTab('other')}
                      >
                        Other Court
                      </button>
                      <button
                        type="button"
                        className={selectedCourtTab === 'supreme' ? 'active' : ''}
                        onClick={() => setSelectedCourtTab('supreme')}
                      >
                        Supreme Court
                      </button>
                    </div>


                 <div className="dropdown-options">
                    {taskSearchTerm && (!Array.isArray(taskFilteredCases) || taskFilteredCases.length === 0) && Array.isArray(associatedCases) && associatedCases.length > 0 && (
                        <div className="no-data">
                        No cases found matching "{taskSearchTerm}"
                        </div>
                    )}


                    {(!Array.isArray(taskFilteredCases) || taskFilteredCases.length === 0) && !taskSearchTerm ? (
                        <div className="no-data">No cases found.</div>
                    ) : (
                        Array.isArray(taskFilteredCases) && taskFilteredCases.map((caseItem, index) => (
                        <div
                            key={index}
                            className={`dropdown-option ${caseItem.courtRef === formData.associatedCase ? 'selected' : ''}`}
                            onClick={() => handleTaskCaseSelect(caseItem)}
                        >
                            <div className="case-info">
                            <div className="case-parties">
                                {caseItem.parties || 'No parties info'}
                            </div>
                            <div className="case-ref">
                                {caseItem.courtRef}
                            </div>
                            {caseItem.caseDate?.caseTypeAndNumber && (
                                <div className="case-number">
                                {caseItem.caseDate.caseTypeAndNumber}
                                </div>
                            )}
                            </div>
                        </div>
                        ))
                    )}
                </div>
                  </div>
                )}
              </div>
            </div>
          </div>


          {/* Branch Selection (Enterprise Only) */}
          {isEnt && (
            <div className="form-section">
              <h3>Branch Information</h3>


              <div className="form-group">
                <label className="label-task">Branch</label>
                <select
                  className="textInput"
                  value={formData.branchId}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, branchId: e.target.value }))
                  }
                  disabled={!!id}
                >
                  <option value="">Select Branch</option>
                  {branchesWithNames.map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}


          {/* Supporting Documents Section */}
          <div className="form-section">
            <h3>Supporting Documents</h3>


            <div className="form-row">
              <div className="form-group upload-document-group">
                <label className="label-task">Upload Document</label>
                <div
                  className="upload-document-container"
                  onClick={handleUploadClick}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) => { if (e.key === 'Enter') handleUploadClick(); }}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    style={{ display: "none" }}
                  />
                  <div className="upload-icon-container">
                    <Upload size={24} className="upload-icon" />
                    <span>Click to upload</span>
                  </div>
                </div>


                {uploading && (
                  <div className="upload-progress">
                    <div className="progress-bar">
                      <div
                        className="progress"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <span>{Math.round(uploadProgress)}% uploaded</span>
                  </div>
                )}


                {formData.documentUrl && (
                  <div className="uploaded-file">
                    <a
                      href={`${process.env.REACT_APP_BACKEND_MAIN_URL_PATH}ftp/view/${formData.documentUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {file ? file.name : "View Uploaded Document"}
                    </a>
                    <button
                      type="button"
                      onClick={handleCancelUpload}
                      className="cancel-upload-button"
                    >
                      Delete Document
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>


          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="submit"
              className="btn-save"
              disabled={isSubmitting || !isFormValid()}
            >
              {isSubmitting ? (taskId ? "Updating..." : "Saving...") : (taskId ? "Update" : "Save")}
            </button>


            <button type="button" className="btn-cancel" onClick={handleCancelTasks}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};